/**
 * Create-org step - combined "name your workspace + your website" onboarding
 * screen. Calls `useOrg().createOrgWithWorkspace(...)` (which runs the
 * `create_org_with_default_workspace` RPC: org + owner membership + default
 * workspace in one transaction), then fires `kickoffBrandResearch`
 * fire-and-forget so the user lands on the dashboard while the worker
 * persists brand context in the background.
 *
 * Idempotency: a single `crypto.randomUUID()` key is generated on mount and
 * reused on generic retries. After a `CreateOrgRetryError` the key is
 * regenerated so the next attempt is a fresh logical create.
 *
 * @module components/setup/CreateOrgStep
 */

import React, { FormEvent, useEffect, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useOrg, OrgLimitReachedError, CreateOrgRetryError } from '../../lib/OrgProvider';
import { useSupabase } from '../../lib/SupabaseProvider';
import { kickoffBrandResearch } from '../../lib/brand-research';
import { captureEvent, captureException } from '../../lib/telemetry/posthog/frontend';
import { ButtonContent } from '../ui/ButtonContent';

interface CreateOrgStepProps {
  /** Optional pre-populated workspace name. */
  initialName?: string;
  /** Called after `createOrgWithWorkspace` resolves; does not wait for research. */
  onComplete?: () => void;
  /** Customize the submit button label (default "Get started"). */
  submitLabel?: string;
  /** Suppress the internal heading when the surrounding container provides one. */
  hideHeading?: boolean;
}

/**
 * Website URL validator. Requires an http(s) scheme + a host with a TLD.
 * Intentionally permissive - backend crawl steps reject anything unresolvable.
 */
const URL_REGEX = /^https?:\/\/.+\..+/;

/**
 * Loads `auth.getUser()` and builds the four `meta_*` fields for
 * `onboarding.organisation_created`. On any failure, logs once and returns
 * empty PII fields (org name preserved).
 */
async function buildOrganisationCreatedMetaForCapture(
  client: SupabaseClient,
  orgDisplayName: string,
): Promise<Record<string, string>> {
  try {
    const { data, error } = await client.auth.getUser();
    if (error !== null) {
      throw error;
    }
    const user = data.user;
    if (user === null) {
      throw new Error('auth.getUser returned no user');
    }
    const md = user.user_metadata as { first_name: string; last_name: string };
    return {
      meta_org_name: orgDisplayName,
      meta_em: (user.email ?? '').trim(),
      meta_fn: md.first_name.trim(),
      meta_ln: md.last_name.trim(),
    };
  } catch (err) {
    const e = err instanceof Error ? err : new Error(String(err));
    console.error('CreateOrgStep.conversionMeta', e);
    captureException(e, 'CreateOrgStep.conversionMeta', {});
    return {
      meta_org_name: orgDisplayName,
      meta_em: '',
      meta_fn: '',
      meta_ln: '',
    };
  }
}

/** Generate a fresh idempotency key. */
function generateKey(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const CreateOrgStep: React.FC<CreateOrgStepProps> = ({
  initialName = '',
  onComplete,
  submitLabel = 'Get started',
  hideHeading = false,
}) => {
  const { createOrgWithWorkspace } = useOrg();
  const { supabase } = useSupabase();

  const [workspaceName, setWorkspaceName] = useState(initialName);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limitReached, setLimitReached] = useState(false);

  /** Persisted idempotency key; regenerated only on `CreateOrgRetryError`. */
  const idempotencyKeyRef = useRef<string>(generateKey());

  useEffect(() => {
    setWorkspaceName(initialName);
  }, [initialName]);

  const trimmedName = workspaceName.trim();
  const trimmedUrl = websiteUrl.trim();
  // URL is optional: valid when the name is set and the URL is either blank
  // or a well-formed http(s) URL. A non-empty malformed URL still blocks
  // submit so the brand-research kickoff never fires on garbage.
  const isValid =
    trimmedName !== '' && (trimmedUrl === '' || URL_REGEX.test(trimmedUrl));

  /**
   * Fire-and-forget brand-research kickoff. The library captures its own
   * failures; this wrapper only guards the session-resolution path.
   */
  const kickoffResearch = async (
    url: string,
    organizationId: string,
    workspaceId: string,
  ): Promise<void> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) {
        // Session evaporated between create-org and kickoff - capture so we
        // can spot the pattern.
        captureException(
          new Error('No access token available for brand research kickoff'),
          'CreateOrgStep.kickoffResearch',
          { organizationId, workspaceId },
        );
        return;
      }

      await kickoffBrandResearch({
        workspaceId,
        url,
        accessToken,
      });
    } catch (err) {
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'CreateOrgStep.kickoffResearch',
        { organizationId, workspaceId },
      );
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setError(null);
    setSubmitting(true);
    try {
      const { orgId, workspaceId } = await createOrgWithWorkspace(
        trimmedName,
        trimmedName,
        trimmedUrl,
        idempotencyKeyRef.current,
      );

      const conversionMeta = await buildOrganisationCreatedMetaForCapture(supabase, trimmedName);
      captureEvent('onboarding.organisation_created', conversionMeta);

      // Detached promise - onComplete fires immediately so the user lands on
      // the dashboard while research finishes in the background. Skipped
      // when no URL was supplied (user can re-research from settings later).
      if (trimmedUrl) {
        void kickoffResearch(trimmedUrl, orgId, workspaceId);
      }

      onComplete?.();
    } catch (err) {
      if (err instanceof OrgLimitReachedError) {
        setLimitReached(true);
        setError("You've reached the organization limit. Contact support if you need more.");
      } else if (err instanceof CreateOrgRetryError) {
        // Prior attempt left the account in a bad state - regenerate so the
        // next click is a fresh logical request.
        idempotencyKeyRef.current = generateKey();
        setError('Something went wrong. Please try again.');
      } else {
        setError("Couldn't create your workspace. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 flex-1 flex flex-col">
      {!hideHeading && (
        <div className="mb-4">
          <h1 className="text-xl font-bold text-foreground">Welcome to holito</h1>
          <p className="text-muted-foreground text-sm mt-1">Tell us about your workspace.</p>
        </div>
      )}

      <div>
        <label htmlFor="workspaceName" className="block text-sm font-medium text-muted-foreground mb-1.5">
          Name your workspace
        </label>
        <input
          id="workspaceName"
          type="text"
          value={workspaceName}
          onChange={(e) => setWorkspaceName(e.target.value)}
          placeholder="Acme Marketing"
          autoFocus
          className="w-full px-4 py-2.5 bg-surface border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:border-volt"
        />
      </div>

      <div>
        <label htmlFor="websiteUrl" className="block text-sm font-medium text-muted-foreground mb-1.5">
          Your website
        </label>
        <input
          id="websiteUrl"
          type="url"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          placeholder="https://acme.com"
          className="w-full px-4 py-2.5 bg-surface border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:border-volt"
        />
        <p className="text-xs text-muted-foreground mt-1">
          We'll use this to draft brand context. Optional.
        </p>
      </div>

      {error && <p className="text-danger text-sm">{error}</p>}

      <div className="flex-1" />
      <button
        type="submit"
        disabled={!isValid || submitting || limitReached}
        aria-busy={submitting}
        className="w-full rounded-lg px-4 py-2.5 button-accent font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ButtonContent loading={submitting}>{submitLabel}</ButtonContent>
      </button>
    </form>
  );
};
